/**
 * InMotion Service Centre — Service Booking Wizard
 * Vanilla JS state machine.  No external dependencies.
 * Mirrors parts-finder.js / repair-tracker.liquid conventions.
 *
 * Steps: model → services → details → slot → confirm → done
 *
 * API calls:
 *   GET  {api}/api/public/booking/models
 *   GET  {api}/api/public/booking/catalog?model=
 *   GET  {api}/api/public/booking/slots
 *   POST {api}/api/public/booking
 *
 * Preview key: read from data-preview-key attr OR ?k= URL param; persisted to sessionStorage.
 * Sent on every fetch as header X-Booking-Preview-Key.
 */

document.addEventListener('DOMContentLoaded', function () {

  var section = document.querySelector('.service-booking');
  if (!section) return;

  /* ── Config ─────────────────────────────────────────────────── */
  var API_BASE = (section.getAttribute('data-api-base') || '').replace(/\/$/, '');
  var SESSION_KEY = 'sb_preview_key';

  // Resolve preview key: section attr → URL ?k= param → sessionStorage → empty
  function resolvePreviewKey() {
    var fromAttr = section.getAttribute('data-preview-key') || '';
    if (fromAttr) { sessionStorage.setItem(SESSION_KEY, fromAttr); return fromAttr; }
    try {
      var fromUrl = new URLSearchParams(window.location.search).get('k') || '';
      if (fromUrl) { sessionStorage.setItem(SESSION_KEY, fromUrl); return fromUrl; }
    } catch (e) {}
    return sessionStorage.getItem(SESSION_KEY) || '';
  }
  var PREVIEW_KEY = resolvePreviewKey();

  /* ── State ───────────────────────────────────────────────────── */
  var state = {
    step:           'model',
    model:          null,   // model key string
    vehicleType:    null,   // 'euc' | 'scooter'
    serialPattern:  null,   // regex string from /models
    serialHint:     null,
    path:           'known',  // 'known' | 'not_sure'
    itemKeys:       [],
    issueDescription: '',
    customerName:   '',
    customerEmail:  '',
    customerPhone:  '',
    serialNumber:   '',
    serialUnknown:  false,
    honeypot:       '',
    dropoffDate:    null,
    dropoffWindow:  null,
    dropoffLabel:   null,
    acknowledge:    false,
    catalog:        null,  // full catalog response for selected model
    bookingToken:   null   // generated UUID, stable per wizard session
  };

  /* ── Utilities ───────────────────────────────────────────────── */

  function escHtml(str) {
    var d = document.createElement('div');
    d.appendChild(document.createTextNode(str || ''));
    return d.innerHTML;
  }

  function fmtMoney(n) {
    return '$' + parseFloat(n || 0).toFixed(2);
  }

  function generateUUID() {
    // RFC 4122 v4 UUID - simple approach
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      var v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // Lazy-generate once per session
  function getBookingToken() {
    if (!state.bookingToken) state.bookingToken = generateUUID();
    return state.bookingToken;
  }

  function apiHeaders() {
    var h = { 'Content-Type': 'application/json' };
    if (PREVIEW_KEY) h['X-Booking-Preview-Key'] = PREVIEW_KEY;
    return h;
  }

  function showEl(el) { if (el) el.style.display = ''; }
  function hideEl(el) { if (el) el.style.display = 'none'; }

  function setError(el, msg) {
    if (!el) return;
    el.textContent = msg || '';
    msg ? showEl(el) : hideEl(el);
  }

  /* ── Step navigation ─────────────────────────────────────────── */

  var STEPS = ['model', 'services', 'details', 'slot', 'confirm', 'done'];

  function goToStep(name) {
    state.step = name;
    STEPS.forEach(function (s) {
      var panel = section.querySelector('[data-step="' + s + '"]');
      if (!panel) return;
      if (s === name) {
        panel.style.display = '';
        panel.classList.add('service-booking__panel--active');
      } else {
        panel.style.display = 'none';
        panel.classList.remove('service-booking__panel--active');
      }
    });
    updateStepBar(name);
    if (name === 'slot') fetchSlots();
    if (name === 'confirm') renderConfirmSummary();
  }

  function updateStepBar(currentName) {
    var stepBar = section.querySelector('[data-step-bar]');
    if (!stepBar) return;
    var indicators = stepBar.querySelectorAll('[data-step-label]');
    var PROGRESS_STEPS = ['model', 'services', 'details', 'slot', 'confirm'];
    var currentIdx = PROGRESS_STEPS.indexOf(currentName);
    indicators.forEach(function (li) {
      var label = li.getAttribute('data-step-label');
      var idx = PROGRESS_STEPS.indexOf(label);
      li.classList.remove(
        'service-booking__step-indicator--active',
        'service-booking__step-indicator--done'
      );
      if (idx < currentIdx) li.classList.add('service-booking__step-indicator--done');
      else if (idx === currentIdx) li.classList.add('service-booking__step-indicator--active');
    });
  }

  /* ── Step: model ─────────────────────────────────────────────── */

  var modelsLoadingEl  = section.querySelector('[data-models-loading]');
  var modelsErrorEl    = section.querySelector('[data-models-error]');
  var eucGridEl        = section.querySelector('[data-vehicle-panel="euc"]');
  var scooterGridEl    = section.querySelector('[data-vehicle-panel="scooter"]');
  var vehicleTabEls    = section.querySelectorAll('[data-vehicle-tab]');
  var unavailableEl    = section.querySelector('[data-unavailable]');
  var wizardEl         = section.querySelector('[data-wizard]');

  function fetchModels() {
    showEl(modelsLoadingEl);
    setError(modelsErrorEl, '');
    hideEl(eucGridEl);
    hideEl(scooterGridEl);

    fetch(API_BASE + '/api/public/booking/models', { headers: apiHeaders() })
      .then(function (r) {
        if (r.status === 404) {
          // Feature is off / gate closed — show neutral state
          hideEl(modelsLoadingEl);
          hideEl(wizardEl);
          showEl(unavailableEl);
          return null;
        }
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        if (!data) return;
        hideEl(modelsLoadingEl);
        renderModels(data.models || []);
      })
      .catch(function (err) {
        hideEl(modelsLoadingEl);
        setError(modelsErrorEl, 'Unable to load device list. Please try again later.');
      });
  }

  function renderModels(models) {
    var eucHtml = '';
    var scooterHtml = '';

    models.forEach(function (m) {
      var label = escHtml(m.display_name || m.model);
      var imgHtml = m.image_url
        ? '<span class="service-booking__model-img">' +
            '<img src="' + escHtml(m.image_url) + '" alt="' + label + '" loading="lazy" width="120" height="120">' +
          '</span>'
        : '<span class="service-booking__model-img service-booking__model-img--placeholder" aria-hidden="true">' +
            '<svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>' +
          '</span>';
      var card = '<button class="service-booking__model-card" type="button"' +
        ' data-model-key="' + escHtml(m.model) + '"' +
        ' data-vehicle-type="' + escHtml(m.vehicle_type) + '"' +
        ' data-serial-pattern="' + escHtml(m.serial_pattern || '') + '"' +
        ' data-serial-hint="' + escHtml(m.serial_hint || '') + '">' +
        imgHtml +
        '<span class="service-booking__model-name">' + label + '</span>' +
        '<span class="service-booking__model-arrow">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>' +
        '</span>' +
        '</button>';

      if (m.vehicle_type === 'scooter') {
        scooterHtml += card;
      } else {
        eucHtml += card;
      }
    });

    if (eucGridEl)     { eucGridEl.innerHTML = eucHtml || '<p class="service-booking__empty-note">No EUC models available.</p>'; showEl(eucGridEl); }
    if (scooterGridEl) { scooterGridEl.innerHTML = scooterHtml || '<p class="service-booking__empty-note">No scooter models available.</p>'; }

    // Attach click handlers to all rendered model cards
    section.querySelectorAll('.service-booking__model-card').forEach(function (card) {
      card.addEventListener('click', function () {
        state.model          = card.getAttribute('data-model-key');
        state.vehicleType    = card.getAttribute('data-vehicle-type');
        state.serialPattern  = card.getAttribute('data-serial-pattern') || null;
        state.serialHint     = card.getAttribute('data-serial-hint') || null;
        // Reset downstream selections
        state.itemKeys = [];
        state.catalog  = null;
        fetchCatalog();
      });
    });
  }

  // Vehicle type tab switching
  vehicleTabEls.forEach(function (tab) {
    tab.addEventListener('click', function () {
      vehicleTabEls.forEach(function (t) { t.classList.remove('service-booking__tab--active'); });
      tab.classList.add('service-booking__tab--active');
      var type = tab.getAttribute('data-vehicle-tab');
      if (eucGridEl)     eucGridEl.style.display     = type === 'euc'     ? '' : 'none';
      if (scooterGridEl) scooterGridEl.style.display = type === 'scooter' ? '' : 'none';
    });
  });

  /* ── Step: services ──────────────────────────────────────────── */

  var catalogLoadingEl  = section.querySelector('[data-catalog-loading]');
  var catalogErrorEl    = section.querySelector('[data-catalog-error]');
  var catalogKnownEl    = section.querySelector('[data-catalog-known]');
  var catalogNotSureEl  = section.querySelector('[data-catalog-not-sure]');
  var knownItemsEl      = section.querySelector('[data-known-items]');
  var diagnosisItemsEl  = section.querySelector('[data-diagnosis-items]');
  var quotePanelEl      = section.querySelector('[data-quote-panel]');
  var quoteTotalEl      = section.querySelector('[data-quote-total]');
  var pathBtns          = section.querySelectorAll('[data-path]');

  pathBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var path = btn.getAttribute('data-path');
      state.path = path;
      state.itemKeys = [];
      pathBtns.forEach(function (b) { b.classList.remove('service-booking__path-btn--active'); });
      btn.classList.add('service-booking__path-btn--active');
      applyPath();
    });
  });

  function applyPath() {
    if (!state.catalog) return;
    if (state.path === 'known') {
      showEl(catalogKnownEl);
      hideEl(catalogNotSureEl);
    } else {
      hideEl(catalogKnownEl);
      showEl(catalogNotSureEl);
    }
    state.itemKeys = [];
    updateQuote();
  }

  function fetchCatalog() {
    if (!state.model) return;
    hideEl(catalogKnownEl);
    hideEl(catalogNotSureEl);
    setError(catalogErrorEl, '');
    showEl(catalogLoadingEl);
    hideEl(quotePanelEl);

    fetch(API_BASE + '/api/public/booking/catalog?model=' + encodeURIComponent(state.model), { headers: apiHeaders() })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        hideEl(catalogLoadingEl);
        state.catalog = data.catalog || {};
        renderCatalog(state.catalog);
        goToStep('services');
      })
      .catch(function () {
        hideEl(catalogLoadingEl);
        setError(catalogErrorEl, 'Unable to load service catalog. Please try again.');
      });
  }

  function renderCatalog(catalog) {
    // Known-need items: replacement where requires_diagnosis == false
    var known = (catalog.replacement || []).filter(function (i) { return !i.requires_diagnosis; });
    if (knownItemsEl) {
      knownItemsEl.innerHTML = known.length ? known.map(function (item) {
        return '<label class="service-booking__item-row">' +
          '<input type="checkbox" value="' + escHtml(item.item_key) + '"' +
          ' data-fee="' + parseFloat(item.fee || 0) + '"' +
          ' data-known-cb> ' +
          '<span class="service-booking__item-name">' + escHtml(item.name_en) + '</span>' +
          '<span class="service-booking__item-fee">' + fmtMoney(item.fee) + '</span>' +
          '</label>';
      }).join('') : '<p class="service-booking__empty-note">No specific services are listed for this model.</p>';
    }

    // Diagnosis items
    var diag = catalog.diagnosis || [];
    if (diagnosisItemsEl) {
      diagnosisItemsEl.innerHTML = diag.length ? diag.map(function (item) {
        return '<label class="service-booking__item-row">' +
          '<input type="checkbox" value="' + escHtml(item.item_key) + '"' +
          ' data-fee="' + parseFloat(item.fee || 0) + '"' +
          ' data-diag-cb> ' +
          '<span class="service-booking__item-name">' + escHtml(item.name_en) + '</span>' +
          '<span class="service-booking__item-fee">' + fmtMoney(item.fee) + (item.is_estimate ? ' <em class="service-booking__est-flag">est.</em>' : '') + '</span>' +
          '</label>';
      }).join('') : '<p class="service-booking__empty-note">Please describe your issue below.</p>';
    }

    // Attach checkbox change handlers
    section.querySelectorAll('[data-known-cb], [data-diag-cb]').forEach(function (cb) {
      cb.addEventListener('change', collectCheckedItems);
    });

    applyPath();
  }

  function collectCheckedItems() {
    var keys = [];
    var selector = state.path === 'known' ? '[data-known-cb]:checked' : '[data-diag-cb]:checked';
    section.querySelectorAll(selector).forEach(function (cb) {
      keys.push(cb.value);
    });
    state.itemKeys = keys;
    updateQuote();
  }

  function updateQuote() {
    var total = 0;
    var selector = state.path === 'known' ? '[data-known-cb]:checked' : '[data-diag-cb]:checked';
    section.querySelectorAll(selector).forEach(function (cb) {
      total += parseFloat(cb.getAttribute('data-fee') || 0);
    });
    if (quoteTotalEl) quoteTotalEl.textContent = fmtMoney(total);
    var hasSelection = state.itemKeys.length > 0;
    hasSelection ? showEl(quotePanelEl) : hideEl(quotePanelEl);
  }

  /* ── Step: details ───────────────────────────────────────────── */

  var serialInputEl     = section.querySelector('[data-serial-input]');
  var serialHintEl      = section.querySelector('[data-serial-hint]');
  var serialWarningEl   = section.querySelector('[data-serial-warning]');
  var serialUnknownCbEl = section.querySelector('[data-serial-unknown-cb]');
  var detailsErrorEl    = section.querySelector('[data-details-error]');

  if (serialInputEl) {
    serialInputEl.addEventListener('input', function () {
      validateSerialFormat();
    });
  }

  if (serialUnknownCbEl) {
    serialUnknownCbEl.addEventListener('change', function () {
      state.serialUnknown = serialUnknownCbEl.checked;
      if (serialInputEl) {
        serialInputEl.disabled = serialUnknownCbEl.checked;
        if (serialUnknownCbEl.checked) serialInputEl.value = '';
      }
      hideEl(serialWarningEl);
    });
  }

  function validateSerialFormat() {
    if (!state.serialPattern || !serialInputEl) return;
    var val = serialInputEl.value.trim();
    if (!val) { hideEl(serialWarningEl); return; }
    try {
      var re = new RegExp(state.serialPattern);
      if (!re.test(val)) {
        showEl(serialWarningEl);
      } else {
        hideEl(serialWarningEl);
      }
    } catch (e) {
      hideEl(serialWarningEl);
    }
  }

  function readDetailsForm() {
    state.customerName  = val('customer_name');
    state.customerEmail = val('customer_email');
    state.customerPhone = val('customer_phone');
    state.serialNumber  = val('serial_number');
    state.honeypot      = val('honeypot');
    state.serialUnknown = section.querySelector('[data-field="serial_unknown"]') ?
                          section.querySelector('[data-field="serial_unknown"]').checked : false;
    if (state.path === 'not_sure') {
      state.issueDescription = val('issue_description');
    }
  }

  function val(fieldName) {
    var el = section.querySelector('[data-field="' + fieldName + '"]');
    return el ? (el.value || '').trim() : '';
  }

  function validateDetails() {
    readDetailsForm();
    if (!state.customerName)  return 'Please enter your name.';
    if (!state.customerEmail || state.customerEmail.indexOf('@') < 0) return 'Please enter a valid email address.';
    if (!state.serialUnknown && !state.serialNumber) return 'Please enter your serial number or check "I don\'t know it".';
    if (state.path === 'not_sure' && !state.issueDescription) return 'Please describe the issue.';
    return null;
  }

  /* ── Step: slot ──────────────────────────────────────────────── */

  var slotsLoadingEl = section.querySelector('[data-slots-loading]');
  var slotsGridEl    = section.querySelector('[data-slots-grid]');
  var slotsErrorEl   = section.querySelector('[data-slots-error]');

  function fetchSlots() {
    showEl(slotsLoadingEl);
    hideEl(slotsGridEl);
    setError(slotsErrorEl, '');

    fetch(API_BASE + '/api/public/booking/slots', { headers: apiHeaders() })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        hideEl(slotsLoadingEl);
        renderSlots(data.slots || []);
      })
      .catch(function () {
        hideEl(slotsLoadingEl);
        setError(slotsErrorEl, 'Unable to load available times. Please try again.');
      });
  }

  function renderSlots(slots) {
    if (!slotsGridEl) return;
    if (!slots.length) {
      slotsGridEl.innerHTML = '<p class="service-booking__empty-note">No drop-off windows are available right now. Please try again soon or <a href="/pages/contact">contact us</a>.</p>';
      showEl(slotsGridEl);
      return;
    }

    // Group by date
    var byDate = {};
    var dateOrder = [];
    slots.forEach(function (s) {
      if (!byDate[s.date]) { byDate[s.date] = []; dateOrder.push(s.date); }
      byDate[s.date].push(s);
    });

    var html = dateOrder.map(function (date) {
      var windows = byDate[date].map(function (s) {
        var selectedClass = (state.dropoffDate === s.date && state.dropoffWindow === s.window) ? ' service-booking__slot--selected' : '';
        return '<button class="service-booking__slot' + selectedClass + '" type="button"' +
          ' data-slot-date="' + escHtml(s.date) + '"' +
          ' data-slot-window="' + escHtml(s.window) + '"' +
          ' data-slot-label="' + escHtml(s.label) + '">' +
          '<span class="service-booking__slot-window">' + escHtml(s.label) + '</span>' +
          '</button>';
      }).join('');
      return '<div class="service-booking__slot-day">' +
        '<div class="service-booking__slot-date">' + escHtml(formatDate(date)) + '</div>' +
        '<div class="service-booking__slot-windows">' + windows + '</div>' +
        '</div>';
    }).join('');

    slotsGridEl.innerHTML = html;
    showEl(slotsGridEl);

    slotsGridEl.querySelectorAll('.service-booking__slot').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.dropoffDate   = btn.getAttribute('data-slot-date');
        state.dropoffWindow = btn.getAttribute('data-slot-window');
        state.dropoffLabel  = btn.getAttribute('data-slot-label');
        slotsGridEl.querySelectorAll('.service-booking__slot').forEach(function (b) {
          b.classList.remove('service-booking__slot--selected');
        });
        btn.classList.add('service-booking__slot--selected');
      });
    });
  }

  function formatDate(isoDate) {
    try {
      // Parse as local date to avoid UTC-day-shift
      var parts = isoDate.split('-');
      var d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    } catch (e) {
      return isoDate;
    }
  }

  /* ── Step: confirm ───────────────────────────────────────────── */

  var confirmSummaryEl = section.querySelector('[data-confirm-summary]');
  var confirmErrorEl   = section.querySelector('[data-confirm-error]');

  function renderConfirmSummary() {
    if (!confirmSummaryEl) return;
    var lines = [
      ['Device', state.model || '—'],
      ['Services', state.itemKeys.join(', ') || '—'],
      ['Drop-off', state.dropoffDate ? (formatDate(state.dropoffDate) + ' · ' + (state.dropoffLabel || state.dropoffWindow)) : '—'],
      ['Name', state.customerName || '—'],
      ['Email', state.customerEmail || '—']
    ];
    if (state.customerPhone) lines.push(['Phone', state.customerPhone]);
    if (state.serialNumber)  lines.push(['Serial', state.serialNumber]);
    else if (state.serialUnknown) lines.push(['Serial', 'Unknown / not activated']);
    if (state.issueDescription) lines.push(['Issue', state.issueDescription]);

    confirmSummaryEl.innerHTML = '<dl class="service-booking__summary-list">' +
      lines.map(function (pair) {
        return '<div class="service-booking__summary-row">' +
          '<dt class="service-booking__summary-label">' + escHtml(pair[0]) + '</dt>' +
          '<dd class="service-booking__summary-value">' + escHtml(pair[1]) + '</dd>' +
          '</div>';
      }).join('') +
      '</dl>';
  }

  /* ── Booking POST ────────────────────────────────────────────── */

  var submitBtn    = section.querySelector('[data-submit]');
  var submitSpinner = submitBtn ? submitBtn.querySelector('.service-booking__spinner--inline') : null;
  var submitText   = submitBtn ? submitBtn.querySelector('.service-booking__btn-text') : null;

  function setSubmitting(on) {
    if (!submitBtn) return;
    submitBtn.disabled = on;
    if (submitSpinner) submitSpinner.style.display = on ? 'inline-block' : 'none';
    if (submitText)    submitText.style.display    = on ? 'none' : '';
  }

  function submitBooking() {
    var ackCb = section.querySelector('[data-field="acknowledge"]');
    if (!ackCb || !ackCb.checked) {
      setError(confirmErrorEl, 'Please tick the acknowledgement checkbox before confirming.');
      return;
    }
    setError(confirmErrorEl, '');
    setSubmitting(true);

    var payload = {
      preview_key:       PREVIEW_KEY,
      model:             state.model,
      item_keys:         state.itemKeys,
      path:              state.path,
      issue_description: state.issueDescription,
      serial_number:     state.serialNumber,
      serial_unknown:    state.serialUnknown,
      customer_name:     state.customerName,
      customer_email:    state.customerEmail,
      customer_phone:    state.customerPhone,
      dropoff_date:      state.dropoffDate,
      window:            state.dropoffWindow,
      acknowledge:       true,
      honeypot:          state.honeypot,
      booking_token:     getBookingToken()
    };

    fetch(API_BASE + '/api/public/booking', {
      method:  'POST',
      headers: apiHeaders(),
      body:    JSON.stringify(payload)
    })
      .then(function (r) {
        if (r.status === 409) {
          // Slot gone — re-fetch slots
          return r.json().then(function () {
            setSubmitting(false);
            setError(confirmErrorEl, 'That drop-off slot was just taken. Please go back and choose a different time.');
            fetchSlots(); // refresh so the re-pick step is ready
          });
        }
        if (r.status === 429) {
          return r.json().then(function () {
            setSubmitting(false);
            setError(confirmErrorEl, 'Too many requests. Please wait a few minutes and try again.');
          });
        }
        if (r.status === 400) {
          return r.json().then(function (d) {
            setSubmitting(false);
            setError(confirmErrorEl, d.error || 'Please check your details and try again.');
          });
        }
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json().then(function (d) {
          setSubmitting(false);
          var caseId = d.case_id || '';
          var caseIdEl = section.querySelector('[data-case-id]');
          if (caseIdEl) caseIdEl.textContent = caseId;
          goToStep('done');
        });
      })
      .catch(function () {
        setSubmitting(false);
        setError(confirmErrorEl, 'Something went wrong. Please try again or contact us directly.');
      });
  }

  /* ── Navigation: Next / Back buttons ────────────────────────── */

  section.addEventListener('click', function (e) {
    var el = e.target;

    // Traverse up to find a button with data-next or data-back
    while (el && el !== section) {
      var next = el.getAttribute('data-next');
      var back = el.getAttribute('data-back');

      if (next) {
        if (!validateCurrentStep(next)) return;
        goToStep(next);
        return;
      }
      if (back) {
        goToStep(back);
        return;
      }
      if (el.hasAttribute('data-submit')) {
        submitBooking();
        return;
      }
      el = el.parentNode;
    }
  });

  function validateCurrentStep(targetStep) {
    if (targetStep === 'details') {
      // Validate services step
      collectCheckedItems();
      if (state.path === 'not_sure') {
        var issueTa = section.querySelector('[data-field="issue_description"]');
        state.issueDescription = issueTa ? issueTa.value.trim() : '';
      }
      if (!state.itemKeys.length) {
        setError(catalogErrorEl, 'Please select at least one service.');
        return false;
      }
      setError(catalogErrorEl, '');
    }
    if (targetStep === 'slot') {
      var detailErr = validateDetails();
      if (detailErr) {
        setError(detailsErrorEl, detailErr);
        return false;
      }
      setError(detailsErrorEl, '');
    }
    if (targetStep === 'confirm') {
      if (!state.dropoffDate || !state.dropoffWindow) {
        setError(slotsErrorEl, 'Please select a drop-off window.');
        return false;
      }
      setError(slotsErrorEl, '');
    }
    return true;
  }

  /* ── Bootstrap ───────────────────────────────────────────────── */

  fetchModels();

}); /* end DOMContentLoaded */
